"use client";

import { LayoutList, LayoutGrid, Menu, PanelLeftClose, PanelLeftOpen, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

export function TopBar({
  title,
  icon,
  color,
  view,
  onViewChange,
  count,
  rightExtra,
  showViewToggle = true,
  onHamburger,
  mobileMenuOpen,
  sidebarCollapsed,
  onToggleSidebar,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search…",
}: {
  title: string;
  icon?: React.ReactNode;
  color?: string;
  view: "list" | "board";
  onViewChange: (v: "list" | "board") => void;
  count?: number;
  rightExtra?: React.ReactNode;
  showViewToggle?: boolean;
  onHamburger?: () => void;
  mobileMenuOpen?: boolean;
  sidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
  /**
   * Optional free-text search. When `onSearchChange` is provided, render a
   * compact search input between the title and the right-side button group.
   * Mirror of the DocsView search pattern. PAUT-63.
   */
  searchValue?: string;
  onSearchChange?: (v: string) => void;
  searchPlaceholder?: string;
}) {
  return (
    <div className="min-h-12 md:h-12 shrink-0 px-2 md:px-5 flex flex-wrap md:flex-nowrap items-center gap-x-2 gap-y-1 py-0.5 md:py-0 border-b border-[var(--border)] bg-[var(--bg)]">
      {onHamburger && (
        <button
          onClick={onHamburger}
          className="md:hidden shrink-0 min-w-11 h-11 px-2 rounded-md hover:bg-[var(--bg-hover)] flex items-center justify-center gap-1.5 text-[12px] font-medium text-[var(--fg-secondary)]"
          aria-label="Open menu"
          aria-expanded={mobileMenuOpen}
          aria-controls="workspace-navigation"
        >
          <Menu size={18} />
          <span>Menu</span>
        </button>
      )}
      {onToggleSidebar && (
        <button
          onClick={onToggleSidebar}
          className="hidden md:flex w-7 h-7 -ml-1 rounded-md hover:bg-[var(--bg-hover)] items-center justify-center text-[var(--fg-tertiary)] hover:text-[var(--fg)] transition-colors"
          title={sidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
        >
          {sidebarCollapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
        </button>
      )}
      {icon && (
        <span
          className="shrink-0 w-5 h-5 rounded-[4px] flex items-center justify-center text-[11px]"
          style={color ? { background: `${color}22`, color } : undefined}
        >
          {icon}
        </span>
      )}
      <h1 className="min-w-0 flex-1 truncate text-[13.5px] font-semibold tracking-tight">{title}</h1>
      {count != null && (
        <span className="text-[11.5px] text-[var(--fg-muted)] bg-[var(--bg-tertiary)] rounded px-1.5 py-0.5">
          {count}
        </span>
      )}
      {onSearchChange && (
        <div className="relative flex-1 max-w-[280px] hidden md:block">
          <Search
            size={12}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--fg-tertiary)]"
          />
          <input
            value={searchValue ?? ""}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md pl-7 pr-7 py-1 text-[12px] outline-none focus:border-[var(--accent)]"
          />
          {searchValue && (
            <button
              onClick={() => onSearchChange("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded flex items-center justify-center text-[var(--fg-tertiary)] hover:text-[var(--fg)] hover:bg-[var(--bg-hover)] transition-colors"
              title="Clear search"
            >
              <X size={11} />
            </button>
          )}
        </div>
      )}

      <div className="ml-auto flex max-w-full flex-wrap items-center gap-1">
        {showViewToggle && (
          <div className="flex items-center rounded-md border border-[var(--border)] overflow-hidden ml-1">
            <button
              onClick={() => onViewChange("list")}
              className={cn(
                "min-w-11 min-h-11 md:min-w-0 md:min-h-0 px-2 py-1 text-[12px] transition-colors",
                view === "list"
                  ? "bg-[var(--bg-active)] text-[var(--fg)]"
                  : "text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)]"
              )}
              title="List view"
            >
              <LayoutList size={13} />
            </button>
            <button
              onClick={() => onViewChange("board")}
              className={cn(
                "min-w-11 min-h-11 md:min-w-0 md:min-h-0 px-2 py-1 text-[12px] transition-colors border-l border-[var(--border)]",
                view === "board"
                  ? "bg-[var(--bg-active)] text-[var(--fg)]"
                  : "text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)]"
              )}
              title="Board view"
            >
              <LayoutGrid size={13} />
            </button>
          </div>
        )}
        {rightExtra}
      </div>
    </div>
  );
}
